import { i18n } from '@lingui/core'
import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Text } from 'designSystem/components/Text'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { documentTypeLabel } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import styled from 'styled-components'

import { DocumentHeaderRevisionChip } from './DocumentHeaderRevisionChip'

export type DocumentInfo = {
  title: string
  system: string
  vendor: string
  docType: string
  drawing: string
  revisionCode: string
  revisionDate: string
}

type DocumentHeaderTitleProps = {
  documentId: string
  customTitle?: string
  showDetails?: boolean
}

const TitleBlock = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
  padding-block: 0.375rem;
`

const TitleCopy = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
`

function buildSubtitle(documentInfo: DocumentInfo): string {
  const parts: string[] = []
  const { revisionCode, revisionDate } = documentInfo
  if (revisionCode && revisionDate) {
    parts.push(t`Revision ${revisionCode} (${revisionDate})`)
  } else if (revisionCode) {
    parts.push(t`Revision ${revisionCode}`)
  } else if (documentInfo.revisionDate) {
    parts.push(documentInfo.revisionDate)
  }
  if (documentInfo.system) parts.push(documentInfo.system)
  if (documentInfo.vendor) parts.push(documentInfo.vendor)
  if (documentInfo.docType) {
    parts.push(
      documentTypeLabel(documentInfo.docType, (descriptor) =>
        i18n._(descriptor)
      )
    )
  }
  if (documentInfo.drawing) parts.push(documentInfo.drawing)
  return parts.join(' \u2022  ')
}

export function DocumentHeaderTitle({
  documentId,
  customTitle,
  showDetails = true,
}: DocumentHeaderTitleProps) {
  useLingui()
  const { document } = useSystemDocumentByBqId(documentId || undefined)
  const entry = document?.entry

  const documentInfo: DocumentInfo = {
    title: entry?.data.document_title || entry?.data.title || '',
    system: entry?.data.system ?? '',
    vendor: entry?.data.vendor_name ?? '',
    docType: entry?.data.document_type ?? '',
    drawing: entry?.data.drawing_number ?? '',
    revisionCode: entry?.data.revision_code ?? '',
    revisionDate: entry?.data.revision_date ?? '',
  }

  const showCustomTitle = customTitle && !documentId

  const displayTitle = showCustomTitle
    ? customTitle
    : documentInfo.title
      ? toTitleCase(documentInfo.title)
      : ''

  return (
    <TitleBlock>
      <TitleCopy>
        <Text as='p' variant='subheading' truncate>
          {displayTitle}
        </Text>
        {showDetails && !showCustomTitle ? (
          <Text as='p' variant='body' color='muted' truncate>
            {buildSubtitle(documentInfo)}
          </Text>
        ) : null}
      </TitleCopy>
      {showDetails && !showCustomTitle ? <DocumentHeaderRevisionChip /> : null}
    </TitleBlock>
  )
}
